import "./SectionUsersSidebarPanel.scss";

import { compareDesc } from "date-fns";
import { partition } from "lodash-es";
import { type FC, type ReactNode, useCallback, useMemo } from "react";

import { SectionUserItem } from "./SectionUserItem";

import { t } from "i18n/i18n";
import { AccordionPanel } from "mds/components/AccordionPanel";
import { IconText } from "mds/components/IconText";
import { PersonSingleIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  EnhancedUserWithSectionActivity,
  selectActiveClassSessionCategory,
  selectScreensharingClassUserId,
  selectUserIdByClassUserId,
} from "store/selectors";
import { sortByActiveStateThenFirstName } from "store/selectors/sorting";
import { usePresentation } from "utils/presentation";

interface DisabledUsersCardProps {
  users: EnhancedUserWithSectionActivity[];
  className?: string;
}

export const DisabledUsersCard: FC<DisabledUsersCardProps> = ({ users, className }) => {
  return (
    <SectionUserCard
      className={className}
      headerCount={users.length.toString()}
      headerIcon={<PersonSingleIcon size="xs" />}
      headerTitle={t("user_options_menu.suspended_accounts")}
      users={users}
    />
  );
};

interface SectionUserCardProps {
  users: EnhancedUserWithSectionActivity[];
  className?: string;
  headerTitle: string;
  headerCount: string;
  headerIcon: ReactNode;
  separateUsersInClassSession?: boolean;
  showEmpty?: boolean;
  startOpen?: boolean;
  filterUsers?: (users: EnhancedUserWithSectionActivity[]) => EnhancedUserWithSectionActivity[];
}

export const SectionUserCard: FC<SectionUserCardProps> = ({
  users,
  className,
  headerTitle,
  headerCount,
  headerIcon,
  separateUsersInClassSession = false,
  showEmpty = false,
  startOpen = false,
  filterUsers = (u: EnhancedUserWithSectionActivity[]) => u,
}) => {
  const { presentingUserId } = usePresentation();
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const screensharingUserId = useAppSelector((state) =>
    selectUserIdByClassUserId(state, screensharingClassUserId),
  );
  const classSessionCategory = useAppSelector(selectActiveClassSessionCategory);

  const filteredUsers = filterUsers(users);

  // For virtual and in-person class sessions, students (handled by the `separateUsersInClassSession` prop)
  // are split into two lists: those who have joined the class session and those who have not
  let usersInClass: EnhancedUserWithSectionActivity[];
  let usersNotInClass: EnhancedUserWithSectionActivity[];
  if (classSessionCategory && separateUsersInClassSession) {
    [usersInClass, usersNotInClass] = partition(filteredUsers, (user) => user.is_in_class_session);
  } else {
    usersInClass = filteredUsers;
    usersNotInClass = [];
  }

  const compareUsers = useCallback(
    (a: EnhancedUserWithSectionActivity, b: EnhancedUserWithSectionActivity) => {
      // Users are shown in this order:
      // 1. The user who is sharing their screen
      if (a.id === screensharingUserId) return -1;
      if (b.id === screensharingUserId) return 1;

      // 2. The user who is presenting
      if (a.id === presentingUserId) return -1;
      if (b.id === presentingUserId) return 1;

      // 3. Users who have raised their hand, ordered by hand_raised_at
      if (a.hand_raised_at && b.hand_raised_at) {
        return compareDesc(b.hand_raised_at, a.hand_raised_at);
      }
      if (a.hand_raised_at) return -1;
      if (b.hand_raised_at) return 1;

      // 4. Users who are following
      if (a.isFollowing && !b.isFollowing) return -1;
      if (!a.isFollowing && b.isFollowing) return 1;

      // 5. Users who are online but not presenting or following
      // 6. Users who are offline
      // 7. Alphabetical by first name
      return sortByActiveStateThenFirstName(a, b);
    },
    [presentingUserId, screensharingUserId],
  );

  // These hashes need to include every field that is used in the sorting function in order for
  // sortedUsersInClass and sortedUsersNotInClass to get re-sorted when necessary
  const usersInClassHash = usersInClass
    .map(
      (user) =>
        `${user.id}-${user.first_name}-${user.last_name}-${user.sectionActivityStatus}-${user.hand_raised_at || ""}-${user.isFollowing}`,
    )
    .join(",");
  const usersNotInClassHash = usersNotInClass
    .map(
      (user) =>
        `${user.id}-${user.first_name}-${user.last_name}-${user.sectionActivityStatus}-${user.hand_raised_at || ""}-${user.isFollowing}`,
    )
    .join(",");

  const sortedUsersInClass = useMemo(
    () => usersInClass.toSorted(compareUsers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersInClassHash, compareUsers],
  );

  const sortedUsersNotInClass = useMemo(
    () => usersNotInClass.toSorted(compareUsers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usersNotInClassHash, compareUsers],
  );

  if (!showEmpty && sortedUsersInClass.length === 0 && sortedUsersNotInClass.length === 0) {
    return null;
  }

  return (
    <AccordionPanel
      childrenWrapperClassName="mt-2.5"
      className={className}
      startOpen={startOpen}
      title={headerTitle}
      titleRightContent={
        <IconText
          className="gap-1 text-sm text-black-tint-20"
          iconEnd={headerIcon}
          text={headerCount}
        />
      }
    >
      {classSessionCategory && separateUsersInClassSession && sortedUsersInClass.length > 0 && (
        <div className="body-xs ml-1 mt-3 text-black-tint-40">{t("av.present_in_session")}</div>
      )}

      {sortedUsersInClass.map((user: EnhancedUserWithSectionActivity) => (
        <SectionUserItem
          key={user.user_id}
          presentingUserId={presentingUserId}
          screensharingUserId={screensharingUserId}
          userId={user.user_id}
        />
      ))}

      {classSessionCategory && separateUsersInClassSession && sortedUsersNotInClass.length > 0 && (
        <div className="body-xs ml-1 mt-3 text-black-tint-40">{t("av.not_present_in_session")}</div>
      )}

      {sortedUsersNotInClass.map((user: EnhancedUserWithSectionActivity) => (
        <SectionUserItem
          key={user.user_id}
          presentingUserId={presentingUserId}
          screensharingUserId={screensharingUserId}
          userId={user.user_id}
        />
      ))}
    </AccordionPanel>
  );
};
